/**
 * SOUND-621 — composer row state for the pends panel.
 *
 * Extracted from PendsPanel because it was UNTESTABLE inside the component, and
 * a bug in it takes the whole panel down rather than degrading: `partial` runs on
 * every render, so a row missing a field threw
 * "Cannot read properties of undefined (reading 'trim')" and blanked the panel.
 *
 * That bug had a root cause worth removing rather than patching: the blank row
 * was constructed in THREE places (initial state, add, remove), so adding
 * `detail` to two of them left the third emitting the old shape. There is now
 * one definition, and the predicates below are TOTAL — they cannot throw on a
 * row whatever shape it arrives in.
 */

/** The one definition of an empty composer row. */
export const blankPendRow = () => ({ category: "", detail: "", description: "" });

/** Read a row field as a trimmed string, whatever the row actually holds. */
const text = (v) => (typeof v === "string" ? v.trim() : "");

/**
 * Normalise anything row-shaped into a complete row.
 *
 * Applied to parsed paste output too, so a parser that omits a field — or gains
 * one later — cannot desynchronise the composer.
 */
export const normalizePendRow = (row = {}) => ({
  category: typeof row.category === "string" ? row.category : "",
  detail: text(row.detail),
  description: typeof row.description === "string" ? row.description : "",
});

/**
 * Rows the operator actually filled in.
 *
 * Complete = a CATEGORY and a description. `detail` is optional: the category
 * alone is a complete pend.
 */
export const filledPendRows = (rows = []) =>
  rows.filter((r) => r?.category && text(r?.description));

/**
 * Is any row HALF-filled?
 *
 * A blank trailing row is normal while typing and is ignored. A half-filled one
 * is a mistake, and the API rejects the whole batch for it (validation is
 * all-or-nothing by design), so it has to surface BEFORE submit rather than as a
 * 400 afterwards.
 */
export const hasPartialPendRow = (rows = []) =>
  rows.some((r) => {
    const category = r?.category || "";
    const description = text(r?.description);
    const detail = text(r?.detail);
    // A category with nothing to say, or words with no category to file them under.
    return (category && !description) || (!category && (description || detail));
  });

/**
 * The payload for POST /pends — category + detail, the vocabulary's shape.
 * `type` is deliberately absent: a composer still sending free text would
 * restart the drift the normalisation cleared.
 */
export const pendRowsToPayload = (rows = []) =>
  filledPendRows(rows).map((r) => ({
    category: r.category,
    detail: text(r.detail),
    description: text(r.description),
  }));
